import { useId, type ReactNode } from 'react';
import dynamic from 'next/dynamic';
import { ProgressiveBlurredImg } from 'components/base';
import { IMAGE_SIZE_MAPPER } from 'utils/constants/ux';

import styles from './style.module.css';
import imageStyles from 'components/base/image/style.module.css';

const ProductCardCarousel = dynamic(() => import('./product-card-carousel').then((mod) => mod.ProductCardCarousel));

type ProductCardMediaProps = {
  productId: string;
  name: string;
  title: string;
  template: 'square' | 'default';
  images: {
    sources: {
      type?: string;
      main?: string;
      hover?: string;
      altText?: string;
      altTextHover?: string;
      isVideo?: boolean;
      sources?: string;
      poster?: string;
    }[];
    full?: string | { main: string; hover: string };
    lg?: string | { main: string; hover: string };
    md?: string | { main: string; hover: string };
    xs?: string | { main: string; hover: string };
  };
  imagesHover?:
    | {
        xs?: string;
        md?: string;
        lg?: string;
        full?: string;
      }
    | undefined;
  withHover?: boolean;
  index?: number;
  loading?: 'lazy' | 'eager';
  sizes?: ReactNode;
  className?: string;
  imageSources?:
    | {
        sources: {
          type: string;
          links: {
            url: string;
            altText: string;
            video: boolean;
          }[];
        }[];
      }
    | undefined;
  target?: string;
};

export function ProductCardMedia({
  productId,
  index = 0,
  title,
  name,
  loading,
  template,
  images,
  imagesHover,
  withHover = true,
  sizes,
  className = '',
  imageSources,
  target,
}: ProductCardMediaProps) {
  const id = useId();
  const srcSetHover: Partial<{
    xs: string;
    md: string;
    lg: string;
    full: string;
  }> = {};

  const isVideo = Array.isArray(images.sources) && images.sources.length === 1 && !!images.sources[0]?.isVideo;
  let videoSource = null;

  const alt = images?.sources?.length && images?.sources[0]?.altText ? images?.sources[0]?.altText : `${productId} - ${name}${title ? ` - ${title}` : ''}`;

  const altHover =
    images?.sources?.length && images?.sources[0]?.altTextHover ? images?.sources[0]?.altTextHover : `${productId} - ${name}${title ? ` - ${title}` : ''}`;

  if (isVideo) {
    [videoSource] = images.sources;
  }

  if (withHover) {
    if (imagesHover) {
      if (imagesHover.xs) srcSetHover.xs = imagesHover.xs;
      if (imagesHover.md) srcSetHover.md = imagesHover.md;
      if (imagesHover.lg) srcSetHover.lg = imagesHover.lg;
      if (imagesHover.full) srcSetHover.full = imagesHover.full;
    } else if (Array.isArray(images.sources)) {
      images.sources.forEach((source) => {
        const t = source.type as keyof typeof srcSetHover | undefined;
        if (t && ['xs', 'md', 'lg', 'full'].includes(t)) {
          srcSetHover[t] = (source.hover ?? source.main) as string;
        }
      });
    }
  }
  const highQualitySrc =
    Array.isArray(images.sources) ?
      images.sources
    : Object.entries(images).reduce(
        (acc, [key, value]) => {
          if (['full', 'lg', 'md', 'xs'].includes(key) && typeof value !== 'string' && value && 'main' in (value as any)) {
            acc.push({
              type: key,
              main: (value as any).main,
              hover: (value as any).hover,
            });
          }
          if (['full', 'lg', 'md', 'xs'].includes(key) && typeof value === 'string') {
            acc.push({
              type: key,
              main: value as string,
            });
          }
          return acc;
        },
        [] as Array<{ type: string; main: string; hover?: string }>,
      );

  if (imageSources && imageSources.sources.length > 0) {
    return (
      <div className={`group relative ${className}`}>
        <ProductCardCarousel imageSources={imageSources} template={template} startAnimation={index === 0} />
        {sizes}
      </div>
    );
  }

  return (
    <div className={`${styles.gridImages} gridImages relative overflow-hidden ${className}`} data-square={template === 'square'}>
      {isVideo ?
        <video
          className={`${styles.gridVideo} ${template === 'square' ? 'self-end' : ''}`}
          autoPlay
          loop
          title={`Video of ${productId}`}
          muted
          playsInline
          poster={videoSource?.poster ?? ''}
          dangerouslySetInnerHTML={{ __html: (videoSource?.sources ?? '') as string }}
        ></video>
      : <ProgressiveBlurredImg
          highQualitySrc={highQualitySrc as unknown as string[]}
          initLoaded={true}
          alt={alt}
          index={index ?? 0}
          loading={loading}
          pictureClassName="lab:bg-neutral"
          target={target ?? ''}
        />
      }
      {withHover && !isVideo && Object.keys(srcSetHover).length > 0 ?
        <picture className={`${imageStyles.image}`}>
          {/* TO DO: En el futuro, hacer lo mismo que hacemos en el source de webp para image/avif */}
          {Object.entries(srcSetHover).reduce<JSX.Element[]>((acc, [srcKey, value]) => {
            if (['md', 'full'].includes(srcKey)) {
              const imageEnd =
                typeof value === 'string' ?
                  value.match(/\.(jpg|png)$/) ?
                    (value.match(/\.(jpg|png)$/) as RegExpMatchArray)[0]
                  : null
                : null;
              if (imageEnd) {
                acc.push(
                  <source
                    key={`${id}-${srcKey}-avif`}
                    type="image/avif"
                    srcSet={(srcSetHover[srcKey as keyof typeof srcSetHover] as string)?.replace(imageEnd, '.avif')}
                    media={IMAGE_SIZE_MAPPER[srcKey as keyof typeof IMAGE_SIZE_MAPPER]}
                  />,
                );
                acc.push(
                  <source
                    key={`${id}-${srcKey}-webp`}
                    type="image/webp"
                    srcSet={(srcSetHover[srcKey as keyof typeof srcSetHover] as string)?.replace(imageEnd, '.webp')}
                    media={IMAGE_SIZE_MAPPER[srcKey as keyof typeof IMAGE_SIZE_MAPPER]}
                  />,
                );
                if (imageEnd === '.png') {
                  acc.push(
                    <source
                      key={`${id}-${srcKey}-png`}
                      data-imgend={imageEnd}
                      type="image/png"
                      srcSet={srcSetHover[srcKey as keyof typeof srcSetHover] as string}
                      media={IMAGE_SIZE_MAPPER[srcKey as keyof typeof IMAGE_SIZE_MAPPER]}
                    />,
                  );
                } else if (imageEnd === '.jpg') {
                  acc.push(
                    <source
                      key={`${id}-${srcKey}-jpg`}
                      data-imgend={imageEnd}
                      type="image/jpg"
                      srcSet={srcSetHover[srcKey as keyof typeof srcSetHover] as string}
                      media={IMAGE_SIZE_MAPPER[srcKey as keyof typeof IMAGE_SIZE_MAPPER]}
                    />,
                  );
                }
              }
            }

            return acc;
          }, [])}
          <img
            src={srcSetHover.md}
            alt={altHover}
            className={`${styles.imageHover} group-hover:visible lab:bg-neutral max-xl:camper:group-hover:invisible`}
            loading="eager"
          />
        </picture>
      : null}
      {sizes}
    </div>
  );
}
